import type { JSX } from 'react';

import { cn } from '@/lib/utils';

const SIZE_CLASSES = {
  sm: 'h-6 w-6 rounded-md',
  md: 'h-7 w-7 rounded-lg',
  lg: 'h-9 w-9 rounded-xl',
} as const;

const GLYPH_CLASSES = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
} as const;

export type BrandMarkSize = keyof typeof SIZE_CLASSES;

interface BrandMarkProps {
  size?: BrandMarkSize;
  /**
   * Surface intent: true (default) renders the inverted white tile for always-dark screens
   * (landing, prejoin, room); false renders the token tile (bg-primary) for light surfaces.
   */
  dark?: boolean;
  className?: string;
}

/** The Syncra video glyph: a rounded conversation tile with a play wedge. Decorative by itself. */
function SyncraGlyph({ className }: { readonly className?: string }): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden className={className}>
      <rect x="1" y="4" width="9" height="8" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 8l4.2-2.6v5.2L10 8z" fill="currentColor" />
    </svg>
  );
}

/**
 * Syncra brand mark: the rounded logo tile holding the conversation glyph. Replaces the earlier
 * Verba gradient mark everywhere (landing header, prejoin top bar).
 */
export function BrandMark({ size = 'md', dark = true, className }: BrandMarkProps): JSX.Element {
  return (
    <span
      aria-hidden
      data-testid="brand-mark"
      className={cn(
        'grid shrink-0 place-items-center',
        dark ? 'bg-white text-zinc-950' : 'bg-primary text-primary-foreground',
        SIZE_CLASSES[size],
        className,
      )}
    >
      <SyncraGlyph className={GLYPH_CLASSES[size]} />
    </span>
  );
}

interface BrandLockupProps {
  size?: BrandMarkSize;
  /** Surface intent, forwarded to the mark; the wordmark stays white on dark surfaces. */
  dark?: boolean;
  className?: string;
}

/**
 * Syncra Workspace lockup: the mark plus the stacked wordmark ("Syncra" over the small-caps
 * "Workspace"), as in the reference mockup's top bars.
 */
export function BrandLockup({
  size = 'md',
  dark = true,
  className,
}: BrandLockupProps): JSX.Element {
  return (
    <span className={cn('flex items-center gap-2.5', className)}>
      <BrandMark size={size} dark={dark} />
      <span className="flex flex-col leading-none">
        <span className="text-[15px] font-semibold tracking-tight text-white">Syncra</span>
        <span className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-zinc-500">
          Workspace
        </span>
      </span>
    </span>
  );
}
